import { useEffect, useState } from "react";
import {
  FileText, Sparkles, ScanSearch, Layers,
  Banknote, Link2, Tags, Scale,
  BarChart3, Package, FileDown, Palette, Users, ShieldCheck,
  Briefcase, Flag, Inbox, History, Archive, Lock,
  Play, Pause,
} from "lucide-react";

const CATEGORIES = [
  {
    id: "parse",
    label: "Parse & categorize",
    eyebrow: "Ingest",
    headline: "Drop a statement. Walk away with a clean ledger.",
    items: [
      { icon: FileText, title: "PDF, CSV & Excel parsing", desc: "Native parsers for tabular CSVs and AI-driven extraction for any bank's PDF layout." },
      { icon: ScanSearch, title: "OCR for scanned PDFs", desc: "Image-only PDFs are auto-OCR'd with Gemini vision before extraction — no manual typing." },
      { icon: Sparkles, title: "Claude Sonnet 4.5 categorization", desc: "Every line item lands in a clean P&L bucket. Override any time in one click." },
      { icon: Layers, title: "Duplicate detection", desc: "Hash-based dedup catches re-uploads, overlapping months, and same-day double-posts." },
      { icon: Tags, title: "Custom categories per tenant", desc: "Build your own chart of accounts. Bulk re-categorize hundreds of transactions in one selection." },
    ],
  },
  {
    id: "connect",
    label: "Reconcile & connect",
    eyebrow: "Connect",
    headline: "Every account, reconciled side-by-side.",
    items: [
      { icon: Banknote, title: "Multi-bank reconciliation", desc: "Checking, savings, credit cards, loans. Opening balances, credits, debits, closing — auto." },
      { icon: Link2, title: "Bank Connect via Teller", desc: "Link real bank accounts via MTLS-secured Teller Connect. Sync transactions on demand." },
      { icon: Scale, title: "Statement vs computed", desc: "Side-by-side reconciliation with mismatch highlighting and one-click auto-match." },
      { icon: Sparkles, title: "Fresh & sync-error indicators", desc: "Green pulse-dot for accounts synced ≤24h, amber pill for connections in error — across every workspace." },
    ],
  },
  {
    id: "firm",
    label: "Multi-tenant firm",
    eyebrow: "Scale",
    headline: "Built for accountants juggling ten books.",
    items: [
      { icon: Briefcase, title: "Unlimited client workspaces", desc: "Switch between every client you serve from one sidebar. Each workspace fully isolated." },
      { icon: Inbox, title: "Cross-tenant review queue", desc: "Single inbox of what needs attention across every client — pending statements, unreconciled txns, AI flags." },
      { icon: Flag, title: "Flag-for-review with shared notes", desc: "Pin any client for follow-up. Teammates get push notifications and see your reason inline." },
      { icon: History, title: "Audit trail per client", desc: "Every flag, every plan change, every login — preserved on an append-only timeline." },
    ],
  },
  {
    id: "report",
    label: "Report & share",
    eyebrow: "Deliver",
    headline: "Accountant-ready exports in two clicks.",
    items: [
      { icon: BarChart3, title: "P&L · Balance Sheet · Cash Flow · Expenses", desc: "Four professional statements with KPI deltas, pie charts, and cumulative cash curves." },
      { icon: Package, title: "Period close package", desc: "One file: cover summary + all four statements. Save it as a reusable template for monthly closes." },
      { icon: FileDown, title: "PDF & Excel exports", desc: "Boardroom-ready PDF and pivotable XLSX for every report and every transaction list." },
      { icon: Palette, title: "White-label branding", desc: "Your logo and brand color on every exported report cover and accent block." },
      { icon: Users, title: "Team & roles", desc: "Invite accountants, bookkeepers, and stakeholders. Owner / Admin / Accountant / Viewer." },
      { icon: Archive, title: "GDPR / CCPA data export", desc: "Encrypted AES-256 ZIP of every tenant record for data-subject access requests." },
      { icon: Lock, title: "Platform-admin impersonation", desc: "Support staff can mint a 15-min token to debug client issues — every action audited." },
      { icon: ShieldCheck, title: "Audit-ready trail", desc: "Every category change, reconciliation, and export is preserved on the transaction record." },
    ],
  },
];

const AUTOPLAY_MS = 6000;

export default function FeaturesTabs() {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const [playing, setPlaying] = useState(true);
  const current = CATEGORIES.find((c) => c.id === active) || CATEGORIES[0];

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      const idx = CATEGORIES.findIndex((c) => c.id === active);
      setActive(CATEGORIES[(idx + 1) % CATEGORIES.length].id);
    }, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [active, playing]);

  return (
    <section id="features" className="border-b" style={{ borderColor: "var(--ll-border)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-black mb-3">What you get</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">
              A complete books-in-a-box, organized the way you actually close.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 text-[var(--ll-ink-soft)] text-base leading-relaxed">
            Twenty-plus capabilities, four deliberate stages — parse, reconcile, scale, and report.
            Every step is auditable, exportable, and quietly elegant.
          </div>
        </div>

        {/* Tab bar */}
        <div
          role="tablist"
          aria-label="Feature categories"
          className="flex flex-wrap gap-1 p-1 border w-fit mb-8"
          style={{ borderColor: "#0b5d9e", background: "#0b5d9e" }}
          data-testid="features-tabs"
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={active === c.id}
              data-testid={`features-tab-${c.id}`}
              onClick={() => { setActive(c.id); setPlaying(false); }}
              className={`text-sm px-4 py-2 transition-colors border ${
                active === c.id
                  ? "bg-white text-[#0b5d9e] border-white font-semibold"
                  : "border-transparent text-white/80 hover:bg-white/15 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Left: list of capabilities */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ochre)] mb-2">{current.eyebrow}</div>
            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight mb-6">{current.headline}</h3>
            <div
              className="grid sm:grid-cols-2 gap-px"
              style={{ background: "var(--ll-border)" }}
              data-testid={`features-grid-${current.id}`}
            >
              {current.items.map((f, idx) => (
                <div
                  key={f.title}
                  className="p-5"
                  style={{
                    background: ["#FFE4E6", "#DBEAFE", "#D1FAE5", "#FEF3C7", "#E0E7FF", "#FCE7F3"][idx % 6],
                  }}
                >
                  <f.icon size={18} className="mb-3" />
                  <div className="font-display font-bold text-base mb-1.5">{f.title}</div>
                  <div className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: animated visual showcase */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <FeaturesAnimation activeId={active} playing={playing} setPlaying={setPlaying} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Animated visual ---------- */
function FeaturesAnimation({ activeId, playing, setPlaying }) {
  return (
    <div
      className="relative border bg-[var(--ll-bg-card)] shadow-[10px_10px_0_0_var(--ll-ink)]"
      style={{ borderColor: "var(--ll-ink)" }}
      data-testid="features-animation"
    >
      <div className="flex items-center gap-1.5 px-3 py-2 border-b" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-sidebar)" }}>
        <span className="w-2.5 h-2.5 rounded-full bg-[#FCA5A5]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FCD34D]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#86EFAC]" />
        <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] font-mono-fin">
          features · live preview
        </span>
        <button
          type="button"
          onClick={() => setPlaying(!playing)}
          aria-label={playing ? "Pause auto-advance" : "Resume auto-advance"}
          data-testid="features-play-toggle"
          className="ml-auto w-5 h-5 grid place-items-center bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f]"
        >
          {playing ? <Pause size={9} /> : <Play size={9} />}
        </button>
      </div>

      <div className="relative h-[420px] overflow-hidden">
        {CATEGORIES.map((c) => (
          <div
            key={c.id}
            className={`absolute inset-0 transition-opacity duration-700 ${activeId === c.id ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            {c.id === "parse" && <ParseAnim active={activeId === "parse"} />}
            {c.id === "connect" && <ConnectAnim active={activeId === "connect"} />}
            {c.id === "firm" && <FirmAnim active={activeId === "firm"} />}
            {c.id === "report" && <ReportAnim active={activeId === "report"} />}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes ff-row { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes ff-tag { 0% { opacity: 0; transform: scale(0.85); } 70% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes ff-bar { from { transform: scaleY(0); } to { transform: scaleY(1); } }
        @keyframes ff-pulse { 0%,100% { opacity: 0.2; } 50% { opacity: 1; } }
      `}</style>
    </div>
  );
}

function ParseAnim({ active }) {
  const rows = [
    { date: "Jan 03", desc: "STRIPE PAYOUT",          amt: "+$8,420.00",  cat: "Sales",     bg: "#ECFDF5", fg: "#065F46" },
    { date: "Jan 04", desc: "WeWork Membership",      amt: "-$1,250.00",  cat: "Rent",      bg: "#FEFCE8", fg: "#854D0E" },
    { date: "Jan 07", desc: "Google Workspace",       amt: "-$72.00",     cat: "SaaS",      bg: "#EFF6FF", fg: "#1E40AF" },
    { date: "Jan 09", desc: "ACME LLC vendor payment",amt: "-$2,100.00",  cat: "Services",  bg: "#FFF7ED", fg: "#9A3412" },
    { date: "Jan 12", desc: "Delta Airlines",         amt: "-$845.20",    cat: "Travel",    bg: "#FEF2F2", fg: "#991B1B" },
    { date: "Jan 15", desc: "Square Receivable",      amt: "+$3,140.55",  cat: "Sales",     bg: "#ECFDF5", fg: "#065F46" },
  ];
  return (
    <div className="absolute inset-0 p-5 bg-white">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] mb-2">AI categorization · live</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            <th className="text-left py-1.5 pr-2">Date</th>
            <th className="text-left py-1.5 pr-2">Description</th>
            <th className="text-right py-1.5 pr-2">Amount</th>
            <th className="text-left py-1.5">Category</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t" style={{
              borderColor: "var(--ll-border)",
              animation: active ? `ff-row 0.45s ease ${0.1 + i * 0.32}s both` : "none",
            }}>
              <td className="py-2 pr-2 font-mono-fin">{r.date}</td>
              <td className="py-2 pr-2">{r.desc}</td>
              <td className="py-2 pr-2 text-right font-mono-fin">{r.amt}</td>
              <td className="py-2">
                <span
                  className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5"
                  style={{
                    background: r.bg, color: r.fg,
                    animation: active ? `ff-tag 0.4s ease ${0.4 + i * 0.32}s both` : "none",
                  }}
                >
                  {r.cat}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConnectAnim({ active }) {
  const banks = [
    { name: "Chase", code: "CH", color: "#005EB8", status: "Linked", pos: { top: "10%", left: "8%" } },
    { name: "Bank of America", code: "BA", color: "#012169", status: "Syncing", pos: { top: "10%", right: "8%" } },
    { name: "Wells Fargo", code: "WF", color: "#D71E2B", status: "Linked", pos: { top: "55%", left: "8%" } },
    { name: "Capital One", code: "C1", color: "#004977", status: "Linked", pos: { top: "55%", right: "8%" } },
  ];
  return (
    <div className="absolute inset-0 p-5 bg-[var(--ll-bg-sidebar)] overflow-hidden">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] mb-3">Teller Connect · multi-bank</div>
      <div className="relative h-[330px]">
        {/* Center hub */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div
            className="w-20 h-20 grid place-items-center text-white font-display font-bold text-2xl"
            style={{ background: "var(--ll-ink)" }}
          >
            L
          </div>
          <div className="text-[9px] uppercase tracking-widest text-center text-[var(--ll-ink-muted)] mt-1">Financials Ready</div>
        </div>
        {/* Connecting lines */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
          <line x1="13" y1="20" x2="50" y2="50" stroke="var(--ll-ink)" strokeWidth="0.3" strokeDasharray="2 1" opacity="0.4" />
          <line x1="87" y1="20" x2="50" y2="50" stroke="var(--ll-ink)" strokeWidth="0.3" strokeDasharray="2 1" opacity="0.4" />
          <line x1="13" y1="68" x2="50" y2="50" stroke="var(--ll-ink)" strokeWidth="0.3" strokeDasharray="2 1" opacity="0.4" />
          <line x1="87" y1="68" x2="50" y2="50" stroke="var(--ll-ink)" strokeWidth="0.3" strokeDasharray="2 1" opacity="0.4" />
        </svg>
        {/* Bank nodes */}
        {banks.map((b, i) => (
          <div
            key={b.code}
            className="absolute"
            style={{
              ...b.pos,
              animation: active ? `ff-row 0.5s ease ${0.2 + i * 0.3}s both` : "none",
            }}
          >
            <div className="flex items-center gap-2 bg-white border px-3 py-2 shadow-sm" style={{ borderColor: "var(--ll-border)" }}>
              <span
                className="w-7 h-7 grid place-items-center text-white text-[10px] font-bold"
                style={{ background: b.color }}
              >
                {b.code}
              </span>
              <div>
                <div className="text-xs font-medium leading-tight">{b.name}</div>
                <div className="text-[9px] uppercase tracking-widest" style={{
                  color: b.status === "Syncing" ? "#9A3412" : "#065F46",
                }}>
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full mr-1"
                    style={{
                      background: b.status === "Syncing" ? "#F97316" : "#10B981",
                      animation: b.status === "Syncing" && active ? "ff-pulse 1.2s ease-in-out infinite" : "none",
                    }}
                  />
                  {b.status}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportAnim({ active }) {
  const bars = [62, 78, 55, 88, 70, 92, 65, 81, 74, 95, 82, 88];
  return (
    <div className="absolute inset-0 p-5 bg-white grid grid-rows-[auto,1fr,auto] gap-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)]">Period close package · revenue by month</div>
      <div className="flex items-end gap-1.5">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-[var(--ll-ink)]"
            style={{
              height: `${h}%`,
              transformOrigin: "bottom",
              animation: active ? `ff-bar 0.5s cubic-bezier(.2,.8,.4,1) ${0.1 + i * 0.06}s both` : "none",
            }}
          />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="border p-2.5" style={{ borderColor: "var(--ll-border)" }}>
          <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Revenue</div>
          <div className="font-mono-fin text-base">$94,531</div>
        </div>
        <div className="border p-2.5" style={{ borderColor: "var(--ll-border)" }}>
          <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Expenses</div>
          <div className="font-mono-fin text-base">$58,221</div>
        </div>
        <div className="p-2.5 text-white" style={{ background: "var(--ll-ink)" }}>
          <div className="text-[9px] uppercase tracking-widest opacity-70">Net</div>
          <div className="font-mono-fin text-base">$36,310</div>
        </div>
      </div>
    </div>
  );
}

function FirmAnim({ active }) {
  const clients = [
    { name: "Northstar Holdings", role: "Owner", txns: "1,284", flag: false, fresh: true },
    { name: "Meridian Capital", role: "Accountant", txns: "542", flag: true, fresh: false },
    { name: "Sienna Labs", role: "Accountant", txns: "318", flag: false, fresh: true },
    { name: "Aubrey & Co", role: "Bookkeeper", txns: "97", flag: false, fresh: false },
  ];
  return (
    <div className="absolute inset-0 p-5 bg-white grid grid-rows-[auto,auto,1fr] gap-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)]">
        Clients portal · review-ready
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="border p-2.5" style={{ borderColor: "var(--ll-border)" }}>
          <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Workspaces</div>
          <div className="font-mono-fin text-base">4</div>
        </div>
        <div className="border p-2.5" style={{ borderColor: "var(--ll-border)" }}>
          <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Txns · 30d</div>
          <div className="font-mono-fin text-base">2,241</div>
        </div>
        <div className="border p-2.5" style={{ borderColor: "var(--ll-border)", background: "var(--ll-ochre-bg)", color: "var(--ll-ochre-ink)" }}>
          <div className="text-[9px] uppercase tracking-widest opacity-80">Flagged</div>
          <div className="font-mono-fin text-base">1</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {clients.map((c, i) => (
          <div
            key={c.name}
            className="flex items-center gap-2 border px-3 py-2.5"
            style={{
              borderColor: c.flag ? "var(--ll-ochre)" : "var(--ll-border)",
              borderWidth: c.flag ? 1.5 : 1,
              animation: active ? `ff-row 0.45s ease ${0.15 + i * 0.18}s both` : "none",
            }}
          >
            <span
              className="w-7 h-7 grid place-items-center text-white text-[10px] font-bold"
              style={{ background: "var(--ll-ink)" }}
            >
              {c.name.charAt(0)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium leading-tight truncate flex items-center gap-1.5">
                {c.name}
                {c.flag && (
                  <span className="text-[9px] uppercase tracking-widest px-1 inline-flex items-center gap-0.5"
                    style={{ background: "var(--ll-ochre-bg)", color: "var(--ll-ochre-ink)" }}>
                    <Flag size={8} fill="var(--ll-ochre)" strokeWidth={0} /> Flagged
                  </span>
                )}
                {c.fresh && (
                  <span className="text-[9px] uppercase tracking-widest px-1 inline-flex items-center gap-0.5"
                    style={{ background: "#D1FAE5", color: "#065F46" }}>
                    <span className="w-1 h-1 rounded-full bg-[#10B981]" /> Fresh
                  </span>
                )}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
                Role · {c.role}
              </div>
            </div>
            <div className="font-mono-fin text-xs text-right">{c.txns}<span className="text-[var(--ll-ink-muted)] text-[9px] uppercase tracking-widest ml-1">txns</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}
